<template>
  <!-- <PageWrapper style="background-color: var(--color-highlight) !important"> -->
  <PageWrapper>
    <!-- The site duotone for the work cards' screenshots (ArticleCard.vue):
         black maps to brown (--color-skin-deep, #38211c) and white to banana
         (--color-banana, #fbe59a), so mid-greys land between the two instead
         of going olive the way a blend does. The dark theme uses the reverse
         (#site-duotone-dark), so a dark screenshot reads yellow with brown
         text there. The values are the two tokens as 0–1 channels; change
         them with the tokens. -->
    <svg class="home-duotone" aria-hidden="true" focusable="false" width="0" height="0">
      <filter id="site-duotone" color-interpolation-filters="sRGB">
        <feColorMatrix
          type="matrix"
          values="0.2126 0.7152 0.0722 0 0
                  0.2126 0.7152 0.0722 0 0
                  0.2126 0.7152 0.0722 0 0
                  0 0 0 1 0"
        />
        <feComponentTransfer>
          <feFuncR type="table" tableValues="0.2196 0.9843" />
          <feFuncG type="table" tableValues="0.1294 0.898" />
          <feFuncB type="table" tableValues="0.1098 0.6039" />
        </feComponentTransfer>
      </filter>
      <filter id="site-duotone-dark" color-interpolation-filters="sRGB">
        <feColorMatrix
          type="matrix"
          values="0.2126 0.7152 0.0722 0 0
                  0.2126 0.7152 0.0722 0 0
                  0.2126 0.7152 0.0722 0 0
                  0 0 0 1 0"
        />
        <feComponentTransfer>
          <feFuncR type="table" tableValues="0.9843 0.2196" />
          <feFuncG type="table" tableValues="0.898 0.1294" />
          <feFuncB type="table" tableValues="0.6039 0.1098" />
        </feComponentTransfer>
      </filter>
    </svg>
    <HomeAvatar />
    <!-- <HeroBanner
      fullvh
      end
      id="hero"
      title="This is my index: work, writing, and thinking on design systems, agentic craft, and the future of design."
    /> -->
    <HeroBanner
      id="hero"
      isHtml
      end
      fullvh
      class="display"
      :title="heroTitle"
      :nobottompadding="true"
      @click="routeHeroLink"
    >
      <!-- Eyebrow slot, commented out entirely — not deleted, either the
           counter or the wordmark+role text here may be revisited later.
           Current state: plain breadcrumb carries "Jacques Ramphal — Design
           Engineer" again (BreadCrumb.vue), same as before this exploration.

      <template #eyebrow>
        <div
          class="hero-counter"
          @mouseenter="pauseHeroCarousel"
          @mouseleave="resumeHeroCarousel"
          @focusin="pauseHeroCarousel"
          @focusout="resumeHeroCarousel"
        >
          <MyButton
            type="ghost"
            size="xs"
            :label="`${String(activeHeroIndex + 1).padStart(2, '0')} / ${String(titles.length).padStart(2, '0')}`"
            :ariaLabel="`Headline ${activeHeroIndex + 1} of ${titles.length}. Click for another.`"
            @click="advanceHeroTitle"
          />
        </div>

        <DynamicText
          as="p"
          text="Jacques Ramphal — Design Engineer"
          isHtml
          :attrs="{ id: 'eyebrow' }"
          class="subtle hero-eyebrow-full"
        />
      </template>
      -->
    </HeroBanner>

    <!-- WRITING SECTION — featured essays pinned first with a "Featured" label
         (`featured: true` in library.json), then newest first. A grid, so
         each piece shows its drawing (motifPlaceholder.ts). -->
    <CardRow2
      class="home-writing"
      title="Writing"
      kind="writing"
      layout="grid"
      :items="publishedEssays"
      :limit="3"
      :viewAllTo="{ path: '/library', hash: '#writing' }"
    />

    <!-- WORK SECTION — case studies, under the writing so the page still
         leads with it. -->
    <CardRow2
      class="home-reveal"
      title="Work"
      kind="work"
      layout="rows"
      :limit="1"
      filterByType="case-study"
      :viewAllTo="{ path: '/library', hash: '#work' }"
    />

    <!-- FEATURED LAB — the effect powering the hero headline, shown with a real screenshot -->
    <!-- Temporarily hidden
    <AnimatedComponent>
      <GridContainer id="featured-lab" style="overflow: visible !important">
        <div
          class="grid-parent"
          style="
            padding-block-end: var(--spacing-md);
            align-items: center;
            grid-template-columns: repeat(3, 1fr);
          "
        >
          <TextBlock style="grid-column: 1 / 3" title="Featured Lab" as="h2" description="" />
          <p class="justify-end" style="align-self: center; white-space: nowrap">
            <router-link :to="{ name: 'PlayIndex' }">View All</router-link>
          </p>
        </div>

        <ImageCard
          variant="split"
          size="large"
          eyebrow="Lab"
          title="Counter Fill"
          description="The effect filling this page's headline, live: a script that colours the enclosed counter spaces inside letterforms — built on real DOM text so it stays accessible."
          filename1="casestudy/counter-fill/counter-fill.png"
          alt="Counter Fill lab — colouring the enclosed counter spaces inside letterforms"
          route="/doc/counter-fill"
          link="/lab/counter-fill/"
          label="Open the live lab"
        />
      </GridContainer>
    </AnimatedComponent>
    -->

    <!-- ABOUT SECTION -->
    <AnimatedComponent class="home-reveal">
      <TextGrid3
        title="About"
        as="h2"
        description=""
        eyebrow1=""
        :detail1="`${careerYears}+ years between design and engineering, designing systems, writing production code, and building with AI.<br/><br/>I care most about the gap between design intent and what actually ships: how decisions survive handoff, how systems scale, and how people stay in control when the tools get smarter. I also coach designers moving closer to systems and code, not because everyone needs to write code, but because fluency changes the kinds of conversations you can have.`"
        label="More about me"
        route="/doc/info"
      />
    </AnimatedComponent>

    <div class="vertical-wordmark" aria-hidden="true">Jacques Ramphal</div>

    <!-- SUBSCRIBE BAR — the compact CardRow-style row (title left, form right).
         It owns its own inset margin and radius via GridWrapper's `inset`. -->
    <div class="home-reveal"><NewsletterBanner /></div>

    <!-- <NewsletterSubscription /> -->
  </PageWrapper>
</template>

<script>
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useHead } from '@vueuse/head';
import work from '@/assets/data/library.json';
import CounterFill from '../../public/lab/counter-fill/counter-fill.js';

// The hero's letter counters (the holes in D, e, g, o, d) filled with ink, the
// text colour, so they follow the theme, in three rings of page colour, like
// contour lines (rings: 3; each band is --hero-counter-inset wide, set in
// typography.scss). The ring colour is the page colour, so a theme switch
// needs no repaint. The fills are vector (CounterFill's default), so the edges
// stay smooth at any size and under pinch zoom. Tried before: the avatar
// colours as a cycle, and a single ring.
const HERO_COUNTERS = {
  stops: ['var(--foreground)'],
  inset: 'var(--hero-counter-inset)',
  rings: 3,
  insetColor: 'var(--background)',
};
import { yearsOfExperience } from '@/utils/experience';
import { featuredThenDate } from '@/utils/libraryData';
import HomeAvatar from '@/components/HomeAvatar/HomeAvatar.vue';
gsap.registerPlugin(ScrollTrigger);

// One description everywhere: index.html, this page, and the RSS feed.
const SITE_DESCRIPTION =
  'This is my work, and what I learned doing it. Essays written from inside design and engineering, dated as they happened.';

export default {
  name: 'HomePage',
  components: { HomeAvatar },
  props: {
    // work: {
    //   type: Object,
    //   required: true,
    // },
  },
  setup() {
    useHead({
      // The tab only. The hero says it as three stops; the og:title stays
      // the name, which is what a shared link should lead with.
      title: 'Jacques Ramphal: design, code, & words',
      meta: [
        {
          name: 'description',
          content: SITE_DESCRIPTION,
        },
        {
          property: 'og:title',
          content: 'Jacques Ramphal',
        },
        {
          property: 'og:description',
          content: SITE_DESCRIPTION,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:url',
          content: 'https://ramphal.design/',
        },
        {
          property: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          property: 'twitter:title',
          content: 'Jacques Ramphal',
        },
        {
          property: 'twitter:description',
          content: SITE_DESCRIPTION,
        },
      ],
    });
  },
  data() {
    return {
      work,
      // Which line is showing. Auto-advances on a timer (see restartHeroTimer),
      // but the timer is just a default — clicking a dot or hovering/focusing the
      // dots overrides or pauses it. Starts from a localStorage round-robin so a
      // repeat visit doesn't always open on the same line.
      activeHeroIndex: 0,
      heroCycleMs: 6000,
      heroPaused: false,
      heroTimer: null,
      heroReducedMotion: false,
      // One pinned line: a record shouldn't rotate its headline. The carousel
      // still works if more lines go back in. Retired lines, kept for reference:
      //   'I design systems &amp; <mark>build the foundations</mark> that make them last.',
      //   'Liking something is easy. <mark>I want to know why.</mark>',
      //   'Machines make things now. <mark>I decide if they’re any good.</mark>',
      //   'I go where the work is hardest, <mark>&amp; stay until it holds.</mark>',
      titles: [
        // Each word goes to its Library section: Words is writing and
        // speaking, and lands on Writing.
        '<a href="/library#work">Design</a>.<br><a href="/library#tools">Code</a>.<br><a href="/library#writing">Words</a>.',
      ],
    };
  },
  computed: {
    careerYears() {
      return yearsOfExperience();
    },
    // Filter work entries to show only Work category (exclude Play)
    filteredWorkEntries() {
      return this.work.entries.filter((entry) => entry.category === 'Work');
    },
    publishedEssays() {
      return this.work.entries
        .filter((e) => e.type === 'article' && e.published !== false)
        .sort(featuredThenDate);
    },
    heroTitle() {
      return this.preventOrphans(this.titles[this.activeHeroIndex]);
    },
  },
  watch: {
    // The title text (and so the CounterFill-painted letterforms) changes whenever
    // the active line does — repaint rather than re-init, so this doesn't stack a
    // second ResizeObserver on top of the one mounted() already set up.
    activeHeroIndex() {
      this.repaintHeroTitle();
    },
  },
  created() {
    const lastIndex = parseInt(localStorage.getItem('lastHeroTitleIndex') ?? '-1', 10);
    this.activeHeroIndex = (lastIndex + 1) % this.titles.length;
    localStorage.setItem('lastHeroTitleIndex', this.activeHeroIndex);
  },
  methods: {
    // Writing is hidden when the page loads and rises into place as you
    // scroll, over the same first half screen that takes the avatar away
    // (HomeAvatar), so one replaces the other: the section comes up from half
    // a screen lower and fades in, at the speed you scroll, and goes back
    // down as you return to the top. The section moves as one piece, slider
    // and all. The sections below it enter as they come up the screen.
    // Skipped for reduced motion.
    revealWriting() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const section = document.querySelector('.home-writing');
      if (!section) return;
      this.writingReveal = gsap.context(() => {
        gsap.fromTo(
          section,
          { y: () => window.innerHeight * 0.5, autoAlpha: 0 },
          {
            y: 0,
            autoAlpha: 1,
            ease: 'power2.out',
            scrollTrigger: {
              start: 0,
              end: () => window.innerHeight * 0.5,
              scrub: 0.3,
              invalidateOnRefresh: true,
            },
          }
        );
        // The sections below (.home-reveal: Work, About, the subscribe bar)
        // enter the same way, each on its own: it rises a quarter screen and
        // fades in as its top comes up from the bottom of the screen, settled
        // by the time it's a third of the way up, and goes back down as you
        // scroll back.
        gsap.utils.toArray('.home-reveal').forEach((el) => {
          gsap.fromTo(
            el,
            { y: () => window.innerHeight * 0.25, autoAlpha: 0 },
            {
              y: 0,
              autoAlpha: 1,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: el,
                start: 'top bottom',
                end: 'top 66%',
                scrub: 0.3,
                invalidateOnRefresh: true,
              },
            }
          );
        });
        // The footer (MainFooter, in App.vue) comes in a column at a time,
        // left to right: the J and the line, then each link column, then the
        // copyright. Scrubbed, so scrolling back takes them out in reverse.
        // It finishes as the page bottoms out, since the footer is the end
        // of the page and can't scroll any higher than that.
        const footer = document.querySelector('.footer-main');
        const columns = footer
          ? footer.querySelectorAll(
              '.footer-logo, #maindetails, #links1, #links2, #links4, #links3, .footer-utility'
            )
          : [];
        if (columns.length) {
          const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
          gsap.fromTo(
            columns,
            { y: 2 * rem, autoAlpha: 0 },
            {
              y: 0,
              autoAlpha: 1,
              ease: 'power2.out',
              stagger: 0.15,
              scrollTrigger: {
                trigger: footer,
                start: 'top bottom',
                end: 'bottom bottom',
                scrub: 0.3,
                invalidateOnRefresh: true,
              },
            }
          );
        }
      });
    },
    // The hero's lines move like the full-screen menu's items. They rise in
    // one after another on load, a short way up and fading in (the menu's
    // .animate.rise), Design first. The scroll sinks them out in the same
    // order, Design first, over the same first half screen that takes the
    // avatar away. The scroll is scrubbed, so coming back to the top plays it
    // backwards: Words returns first and Design last.
    // CounterFill wraps the whole title in one span, with a <br> between the
    // lines, so each line is the pieces between two breaks (the word's link
    // and its full stop), moved together. Skipped for reduced motion.
    animateHeroTitle() {
      const text = document.querySelector('#hero-text');
      const reveal = () => text && text.classList.remove('hero-pending');
      if (this.heroReducedMotion || this.heroMotion) return reveal();
      const title = document.querySelector('#hero-title');
      const root = title && (title.querySelector('.cf-glue') || title);
      const lines = [];
      let line = 0;
      for (const node of root ? root.children : []) {
        if (node.tagName === 'BR') line += 1;
        else if (node.tagName !== 'svg' && node.tagName !== 'CANVAS') {
          // A link can't fade (typography.scss holds every link at full
          // opacity), so its word span moves instead.
          const parts = node.tagName === 'A' ? [...node.children] : [node];
          for (const part of parts) {
            part.dataset.heroLine = line;
            lines.push(part);
          }
        }
      }
      if (!lines.length) return reveal();
      const lineOf = (i, el) => Number(el.dataset.heroLine);
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      this.heroMotion = gsap.context(() => {
        const sink = () =>
          gsap.fromTo(
            lines,
            { y: 0, autoAlpha: 1 },
            {
              y: 1.2 * rem,
              autoAlpha: 0,
              ease: 'power1.in',
              stagger: (i, el) => lineOf(i, el) * 0.15,
              immediateRender: false,
              scrollTrigger: {
                start: 0,
                end: () => window.innerHeight * 0.5,
                scrub: 0.3,
                invalidateOnRefresh: true,
              },
            }
          );
        if (window.pageYOffset > 0) {
          reveal();
          sink();
          ScrollTrigger.refresh();
          return;
        }
        gsap.set(lines, { y: 1.6 * rem, autoAlpha: 0 });
        reveal();
        gsap.fromTo(
          lines,
          { y: 1.6 * rem, autoAlpha: 0 },
          {
            y: 0,
            autoAlpha: 1,
            duration: 0.26,
            ease: 'power2.out',
            stagger: (i, el) => lineOf(i, el) * 0.06,
            onComplete: sink,
          }
        );
      });
    },
    advanceHeroTitle() {
      this.activeHeroIndex = (this.activeHeroIndex + 1) % this.titles.length;
      localStorage.setItem('lastHeroTitleIndex', this.activeHeroIndex);
      this.restartHeroTimer();
    },
    restartHeroTimer() {
      clearTimeout(this.heroTimer);
      if (this.heroReducedMotion || this.heroPaused || this.titles.length < 2) return;
      this.heroTimer = setTimeout(this.advanceHeroTitle, this.heroCycleMs);
    },
    pauseHeroCarousel() {
      this.heroPaused = true;
      clearTimeout(this.heroTimer);
    },
    resumeHeroCarousel() {
      this.heroPaused = false;
      this.restartHeroTimer();
    },
    // CounterFill.paint() repaints one element from its current text, unlike
    // .init() which also (re-)registers a ResizeObserver — safe to call on every
    // title change, where .init() isn't.
    repaintHeroTitle() {
      this.$nextTick(() => {
        const h1 = document.querySelector('h1.title');
        if (h1) {
          CounterFill.paint(h1, { 'hero-title': HERO_COUNTERS });
        }
      });
    },
    // The hero title is v-html, so its links are plain anchors that would reload
    // the page. Hand internal ones to the router instead.
    routeHeroLink(event) {
      const a = event.target.closest?.('a[href^="/"]');
      if (!a || event.metaKey || event.ctrlKey || event.shiftKey || event.button) return;
      event.preventDefault();
      this.$router.push(a.getAttribute('href'));
    },
    // Bind single-char words (I, A) to the next word, and the last two words to
    // each other, so neither strands alone at a line end. The title is HTML, so
    // only the text between tags is touched: a no-break space inside a tag
    // (between <a and href) breaks the link, which is what happened when the line
    // ended on a link.
    preventOrphans(html) {
      const parts = html.split(/(<[^>]*>)/);
      const isText = (i) => i % 2 === 0;
      for (let i = 0; i < parts.length; i += 2) {
        parts[i] = parts[i].replace(/\b([IA])\s(?=[A-Za-z])/g, '$1 ');
      }
      // The last breaking space in the text, wherever the tags fall.
      for (let i = parts.length - 1; i >= 0; i -= 1) {
        if (!isText(i)) continue;
        const m = parts[i].match(/[ \t\n]+(?=[^ \t\n]*$)/);
        if (m) {
          parts[i] = parts[i].slice(0, m.index) + ' ' + parts[i].slice(m.index + m[0].length);
          break;
        }
      }
      return parts.join('');
    },
  },
  mounted() {
    this.heroReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    this.restartHeroTimer();

    // ?cf=debug outlines each hero word by how CounterFill drew it: green for
    // vector, red for the canvas fallback. For checking a device by eye.
    if (/[?&]cf=debug\b/.test(window.location.search)) {
      const st = document.createElement('style');
      st.textContent =
        '#hero-title [data-cf-mode="vector"]{outline:2px solid #2a2}#hero-title [data-cf-mode="canvas"]{outline:2px solid #d22}';
      document.head.appendChild(st);
    }

    // Hidden until its lines rise in (animateHeroTitle), so the words don't
    // show and then jump; a second at most, in case that never runs.
    const heroText = document.querySelector('#hero-text');
    if (heroText && !this.heroReducedMotion) {
      heroText.classList.add('hero-pending');
      this.heroPendingTimer = setTimeout(() => heroText.classList.remove('hero-pending'), 1000);
    }

    this.$nextTick(() => {
      const h1 = document.querySelector('h1.title');
      if (h1) {
        // CounterFill keys its fills by id.
        h1.id = 'hero-title';
        h1.classList.add('wrap-multi');
        document.fonts.ready.then(() => {
          // The lines move once CounterFill has rebuilt the title into its
          // word spans; before that, they'd be nodes it's about to replace.
          Promise.resolve(CounterFill.init({ 'hero-title': HERO_COUNTERS })).then(() =>
            requestAnimationFrame(this.animateHeroTitle)
          );
        });
      }
    });

    this.$nextTick(() => requestAnimationFrame(this.revealWriting));

    // this.htmlContent = marked(content);
    const fadeInUp = gsap.utils.toArray('.fadeInUp');
    const fadeInDown = gsap.utils.toArray('.fadeInDown');
    const fadeInRight = gsap.utils.toArray('.fadeInRight');
    const fadeInLeft = gsap.utils.toArray('.fadeInLeft');
    const parallaxBack = gsap.utils.toArray('.parallaxBack');
    const parallaxFront = gsap.utils.toArray('.parallaxFront');

    fadeInUp.forEach((fadeInUp) => {
      gsap.from(fadeInUp, {
        scrollTrigger: {
          trigger: fadeInUp,
          start: 'top bottom',
          end: 'top 50%',
          scrub: 1,
          toggleActions: 'restart pause reverse pause',
        },
        autoAlpha: 0,
        y: 100,
        duration: 3,
        ease: 'none',
      });
    });
    fadeInDown.forEach((fadeInDown) => {
      gsap.from(fadeInDown, {
        scrollTrigger: {
          trigger: fadeInDown,
          start: 'top bottom',
          end: 'top 50%',
          scrub: 1,
          toggleActions: 'restart pause reverse pause',
        },
        autoAlpha: 0,
        y: -100,
        duration: 3,
        ease: 'none',
      });
    });
    fadeInRight.forEach((fadeInRight) => {
      gsap.from(fadeInRight, {
        scrollTrigger: {
          trigger: fadeInRight,
          start: 'top bottom',
          end: 'top 50%',
          scrub: 1,
          toggleActions: 'restart pause reverse pause',
        },
        autoAlpha: 0,
        x: 100,
        duration: 3,
        ease: 'none',
      });
    });
    fadeInLeft.forEach((fadeInLeft) => {
      gsap.from(fadeInLeft, {
        scrollTrigger: {
          trigger: fadeInLeft,
          start: 'top bottom',
          end: 'top 50%',
          scrub: 1,
          toggleActions: 'restart pause reverse pause',
        },
        autoAlpha: 0,
        x: -100,
        duration: 3,
        ease: 'none',
      });
    });
    parallaxBack.forEach((parallaxBack) => {
      gsap.to(parallaxBack, {
        scrollTrigger: {
          trigger: parallaxBack,
          scrub: true,
        },
        yPercent: 10,
        duration: 3,
        ease: 'none',
      });
    });
    parallaxFront.forEach((parallaxFront) => {
      gsap.to(parallaxFront, {
        scrollTrigger: {
          trigger: parallaxFront,
          scrub: true,
        },
        yPercent: -10,
        duration: 3,
        ease: 'none',
      });
    });
  },
  beforeUnmount() {
    clearTimeout(this.heroTimer);
    clearTimeout(this.heroPendingTimer);
    if (this.writingReveal) this.writingReveal.revert();
    if (this.heroMotion) this.heroMotion.revert();
  },
};
</script>

<style lang="scss">
@media only screen and (min-width: 1201px) {
}

/* Hero line in the heading face, sized from the viewport alone. One size for
   every line the carousel can show, so a short line and a long one never
   render at different sizes; it just uses more of the screen as the screen
   grows. Spans all twelve columns. 9.5vw is the largest size that keeps the
   current line to three lines at every desktop width (1024 to 1920); 10vw
   breaks to four at 1280. Re-check if the line changes. */
#hero {
  --hero-fontSize: clamp(var(--size-12), 9.5vw, 40rem);
}

/* The home hero's line sits a little closer to the top than HeroBanner's
   default (--spacing-xl above the text). */
#hero #hero-text {
  margin-block-start: calc(var(--spacing-md) + var(--spacing-sm));
  /* HeroBanner lifts its text to 1000, level with the full-screen menu. Here
     it stays low, so the avatar (HomeAvatar, 500) stands in front of the line
     and still goes behind the menu (999 and up). */
  z-index: 1;
}
/* The lines rise in themselves (animateHeroTitle), so the text block's own
   fade is off here. The pieces of each line are inline, and a transform
   needs a box, so they're inline blocks. */
#hero #hero-text.animate {
  animation: none;
}
#hero #hero-text.hero-pending {
  visibility: hidden;
}
#hero #hero-title [data-hero-line] {
  display: inline-block;
}

/* Full height on desktop only (fullvh). On phones the hero is as tall as its
   line, so Writing can rise in right under it as the avatar leaves. */
@media only screen and (max-width: 767px) {
  #hero .herobanner--fullvh {
    min-height: 0;
  }
}

.home-duotone {
  position: absolute;
  inline-size: 0;
  block-size: 0;
  overflow: hidden;
}

/* Portrait (phones, tall narrow windows): width is scarce and height is not,
   so the three-line rule gives way. The line wraps to five or six lines at
   about 16vw, filling the width, and the height term stops a tall tablet from
   running to seven. Phones (under 768px) are sized in typography.scss
   (#hero-title) to break in four set lines instead. */
@media (orientation: portrait) {
  #hero {
    --hero-fontSize: min(16vw, 9svh);
  }
}

#hero h1.title {
  font-family: var(--fontFamily-heading);
  font-size: var(--hero-fontSize) !important;
  font-weight: var(--display-fontWeight);
  font-variation-settings: normal;
  letter-spacing: -0.02em; /* was -0.035em; looser to match the lighter weight */
  line-height: var(--lineHeight-shortest);
}

#hero #hero-text span {
  grid-column: 1 / -1 !important;
}

/* counter-fill: required CSS for multi-line mode */
h1.wrap-multi {
  position: relative;
  display: block;

  canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
  }

  .cf-word {
    position: relative;
    z-index: 1;
    display: inline-block;
  }
}

/* Hero rotating-title counter. Replaced the dots/progress-bar carousel
   pattern entirely — it fought the site's own visual language (text as the
   interface, no decorative chrome elsewhere) and the fill colour never
   rendered reliably visible as a small shape, despite the same colour
   working fine everywhere else it's used as text. A MyButton (ghost/xs),
   same as PresenterBar's own nav controls, not a hand-rolled <button> —
   and muted the same way PresenterBar mutes its own counter span
   (opacity, not a separate translucent colour token), since a ghost
   button is full-strength/bold by default and this needs to read as
   secondary to the headline, not another button competing with it. */
.hero-counter {
  // In the eyebrow slot now, not below the title — #eyebrow already carries
  // its own spacing (margin-block-end: 4rem), so no margin of this element's
  // own is needed.
  display: inline-block;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

/* .display p { font-size: var(--font-600); } in typography.scss only applies
   at min-width: 1441px, and --font-600 itself is a flat var(--size-7), not
   the clamp() the "FLUID FONT SIZES" comment above it implies — the token
   that would have made this fluid is commented out in _config.scss. So below
   1441px the eyebrow got no rule at all (default ~1rem), then jumped straight
   to 2.8rem at the threshold: a snap, not a scale. This clamp covers the gap
   properly, ramping continuously between this file's own two breakpoints
   (768px / 1441px, already used throughout for the hero) and landing on the
   same 2.8rem --size-7 was giving above 1441px, so nothing changes at the
   top end — only how it gets there. */
.hero-eyebrow-full {
  // !important: without it, .display p (typography.scss, >=1441px, higher
  // specificity) would still win above that breakpoint. Both resolve to the
  // same 2.8rem there regardless, but this rule should be the one actually
  // governing the full range, not just coincidentally matching at the top.
  font-size: var(--font-hero-eyebrow) !important;
  --reading-fontWeight: var(--font-hero-eyebrow-weight);
  font-weight: var(--font-hero-eyebrow-weight) !important;
}

/* Eyebrow identity line: desktop only. Tried a name-only mobile variant
   first; dropped it in favour of not showing the same identity at two
   different sizes on a small screen — the breadcrumb (BreadCrumb.vue)
   un-hides itself on mobile and carries this there instead. Same
   breakpoint this file already uses for .vertical-wordmark below. */
@media only screen and (max-width: 767px) {
  .hero-eyebrow-full {
    display: none;
  }
}

.hero-counter:hover,
.hero-counter:focus-within {
  opacity: 1;
}

.vertical-wordmark {
  position: fixed;
  display: none;
  inset-inline-start: var(--spacing-xs);
  inset-block-start: var(--spacing-sm);
  writing-mode: vertical-rl;
  transform: rotate(270deg);
  font-size: var(--font-xs);
  --reading-fontWeight: var(--font-xs-weight);
  font-weight: var(--font-xs-weight);
  color: var(--foreground);
  letter-spacing: 0.05em;
  z-index: 10;
  pointer-events: none;
  user-select: none;

  @media only screen and (max-width: 767px) {
    display: none;
  }
}
</style>
